import { useState } from 'react';

import { Send, Trash2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAddComment, useDeleteComment } from '@/hooks/useCallComments';
import { useAuthStore } from '@/stores/auth.store';
import { formatDateTime } from '@/utils/format';

import type { CallComment } from '@repo/shared-types';

interface CallCommentsProps {
  callId: string;
  comments?: CallComment[];
  isLoading?: boolean;
}

export const CallComments = ({ callId, comments = [], isLoading = false }: CallCommentsProps) => {
  const addCommentMutation = useAddComment(callId);
  const deleteCommentMutation = useDeleteComment(callId);
  const [newComment, setNewComment] = useState('');
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await addCommentMutation.mutateAsync(newComment);
      setNewComment('');
      toast({
        variant: 'success',
        title: 'Comment added',
        description: 'Your comment has been posted successfully.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to add comment',
        description: 'Please try again later.',
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCommentMutation.mutateAsync(commentId);
      toast({
        variant: 'success',
        title: 'Comment deleted',
        description: 'The comment has been removed.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to delete comment',
        description: 'Please try again later.',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comments</CardTitle>
        <CardDescription>Internal notes and discussion</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Add Comment Form */}
          <div className="flex gap-4">
            <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <Users className="text-primary h-5 w-5" />
            </div>
            <div className="flex-1 space-y-2">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                placeholder="Add a comment... (Press Enter to send, Shift+Enter for new line)"
                className="border-input placeholder:text-muted-foreground focus-visible:ring-ring min-h-[100px] w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || addCommentMutation.isPending}
                >
                  {addCommentMutation.isPending ? (
                    'Posting...'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Post Comment
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Comments List */}
          <div className="space-y-6">
            {isLoading ? (
              <div className="text-muted-foreground py-4 text-center text-sm">
                Loading comments...
              </div>
            ) : comments && comments.length > 0 ? (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                    <span className="font-medium text-slate-600 dark:text-slate-300">
                      {comment.user?.name
                        ? comment.user.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)
                        : 'U'}
                    </span>
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">
                          {comment.user?.name || 'Unknown User'}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {formatDateTime(comment.created_at)}
                        </span>
                      </div>
                      {user?.id === comment.user_id && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Delete Comment</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete this comment? This action cannot be
                                undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                              <Button
                                variant="destructive"
                                onClick={() => handleDeleteComment(comment.id)}
                                disabled={
                                  deleteCommentMutation.isPending &&
                                  deleteCommentMutation.variables === comment.id
                                }
                              >
                                {deleteCommentMutation.isPending &&
                                deleteCommentMutation.variables === comment.id
                                  ? 'Deleting...'
                                  : 'Delete'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted-foreground py-8 text-center text-sm">
                No comments yet. Be the first to add one!
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
